import { memo, useEffect, useState } from "react";

import { Img, type ImageProps } from "@chakra-ui/react";

/**
 * Props interface for DefaultImage component
 */
interface DefaultImageProps {
  /** Source URL of the image */
  src: string | null;
  /** Type of image ('person' or any other value) */
  type?: 'person';
  /** Additional props to pass to the underlying Chakra UI Img component */
  options?: Omit<ImageProps, 'src'>;
}

/**
 * A component that handles image loading with fallback support.
 * @see {@link https://chakra-ui.com/docs/components/image/usage} - Chakra UI Image component
 */
const DefaultImage = ({ src, type, options }: DefaultImageProps) => {
  const [imgSrc, setImgSrc] = useState<string | null>(null);

  const noImage =
    type === "person" ? "/images/no-avatar.svg" : "/images/no-company.svg";

  useEffect(() => {
    const checkImageExists = async (url: string): Promise<boolean> => {
      if (typeof window !== "undefined") {
        return new Promise((resolve) => {
          const img = new window.Image();
          img.onload = () => resolve(true);
          img.onerror = () => resolve(false);
          img.src = url;
        });
      }
      return false;
    };

    if (!src) {
      setImgSrc(noImage);
      return;
    }

    checkImageExists(src).then((isImageExists) => {
      setImgSrc(isImageExists ? src : noImage);
    });

    return () => {};
  }, [src, noImage]);

  // Don't render until we have a valid source
  if (!imgSrc) return null;

  return <Img src={imgSrc} {...options} />;
};

export default memo(DefaultImage);
